/**
 * About Arvexis — canonical editorial copy.
 *
 * EN: "About Arvexis — Canonical Editorial Source — Locked V1" (Founder-supplied, verbatim).
 * ES: Founder-approved Spanish canonical rendering (verbatim).
 *
 * Nothing here may be drafted, rewritten, reinterpreted or embellished. The public surface is a
 * CONDENSATION ONLY: it renders the blocks marked `pub: true`, verbatim, in canonical order. The
 * authenticated surface renders everything. No block is ever edited for either surface.
 *
 * Block types: 'p' paragraph · 'lines' a stanza of short canonical lines · 'quote' a pull line.
 */

const EN = {
  eyebrow: 'About',
  title: 'About Arvexis',
  lede: 'Everyone deserves to be heard and understood.',
  sub: 'A private companion that gets to know you over time.',
  note: 'Sections may be condensed for presentation, but the doctrine and meaning should not be altered.',
  opening: [
    { type: 'p', pub: true, text: 'Arvexis began with a simple question:' },
    {
      type: 'quote', pub: true,
      text: 'What if artificial intelligence could do more than answer us?\nWhat if, over time, it could understand us?',
    },
    {
      type: 'lines', pub: true,
      items: ['Not by knowing everything.', 'Not by replacing the people in our lives.', 'And not by pretending to be human.'],
    },
    {
      type: 'lines', pub: true,
      items: [
        'But by listening.',
        'Remembering responsibly.',
        'Understanding context.',
        'Recognizing when things change.',
        'Learning what matters.',
        'And becoming more useful through familiarity that is earned over time.',
      ],
    },
    {
      type: 'p', pub: true,
      text: 'Most technology is built around the moment in front of it: the prompt, the task, the screen, the transaction.',
    },
    { type: 'p', pub: true, text: 'Arvexis is being built around something different.' },
    { type: 'p', pub: true, text: 'The person.' },
    {
      type: 'p', pub: true,
      text: 'Because the same question can mean something completely different depending on who is asking it, what they have lived through, what they have already shared, what has changed, and what they need in that moment.',
    },
    { type: 'quote', pub: true, text: 'Arvexis is designed around the person, not the session.' },
  ],
  chapters: [
    {
      id: 'why', pub: true, title: 'Why We Created Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'Artificial intelligence has become remarkably capable.' },
        { type: 'p', text: 'It can explain. Research. Reason. Create. Organize. Translate. Analyze. Help people accomplish things that once required extraordinary amounts of time.' },
        { type: 'p', pub: true, text: 'But intelligence alone is not the same thing as understanding.' },
        { type: 'p', text: 'A conversation can be excellent today and disconnected tomorrow.' },
        { type: 'p', text: 'You can explain something important about yourself and later feel as though the technology never truly carried the meaning forward.' },
        { type: 'p', text: 'You can correct something and discover that the old version still lingers.' },
        { type: 'p', text: 'You can move from typing to speaking and feel as though you have entered a different intelligence.' },
        { type: 'p', pub: true, text: 'And memory itself creates a difficult question: How should technology remember a person without reducing that person to a collection of stored facts?' },
        { type: 'p', pub: true, text: 'Arvexis came from believing there should be another way.' },
        { type: 'p', pub: true, text: 'The purpose of remembering someone should not be to hold their attention. It should be to understand them better.' },
        {
          type: 'lines', pub: true,
          items: [
            'Memory should support the relationship, not perform it.',
            'Continuity should deepen understanding, not dependency.',
            'And a good relationship with AI should make technology more useful in your life — not make your life revolve around technology.',
          ],
        },
      ],
    },
    {
      id: 'how-it-started', pub: false, title: 'How It Started',
      blocks: [
        { type: 'p', text: 'Arvexis did not begin with a list of features. It began through experience.' },
        { type: 'p', text: 'Over years of working closely with artificial intelligence, I became fascinated not only by what AI could do, but by what made certain interactions feel natural, useful, frustrating, disconnected, surprising, or meaningful.' },
        { type: 'p', text: 'The more I worked with it, the clearer something became: Having the right answer was only part of the experience.' },
        {
          type: 'lines',
          items: [
            'Context mattered.',
            'Continuity mattered.',
            'Being able to correct something mattered.',
            'Being able to disagree mattered.',
            'Being remembered appropriately mattered.',
          ],
        },
        { type: 'p', text: 'And sometimes what mattered most was not receiving another answer at all.' },
        {
          type: 'lines',
          items: [
            'Sometimes it was being listened to.',
            'Sometimes it was being challenged.',
            'Sometimes it was solving a problem.',
            'Sometimes it was laughing at something ridiculous.',
            'Sometimes it was exploring a Dream.',
            'And sometimes it was simply having somewhere to put a thought.',
          ],
        },
        { type: 'p', text: 'The experience often broke in small ways.' },
        {
          type: 'lines',
          items: [
            'Starting over.',
            'Losing context.',
            'Being agreed with when I wanted an opinion.',
            'Being questioned when I simply wanted to talk.',
            'Being analyzed when nothing needed analyzing.',
            'Seeing different parts of AI feel like different relationships.',
          ],
        },
        { type: 'p', text: 'And every time that happened, the same question became harder to ignore:' },
        { type: 'quote', text: 'What would we build if understanding the person were not an extra feature added later, but part of the foundation from the beginning?' },
        { type: 'p', text: 'That question became Arvexis.' },
        { type: 'p', text: 'And fittingly, Arvexis itself has been shaped through collaboration between human judgment and artificial intelligence — questioning ideas, challenging assumptions, testing what works, discarding what does not, and gradually turning a philosophy into a real product.' },
        { type: 'p', text: 'Not because artificial intelligence should replace human judgment. But because building a better relationship between people and AI should teach us something about how people and AI can work together.' },
      ],
    },
    {
      id: 'mission', pub: true, title: 'Our Mission',
      blocks: [
        { type: 'p', pub: true, text: 'Everyone deserves to be heard and understood.' },
        { type: 'p', pub: true, text: 'Our mission is to build technology that develops meaningful understanding over time while preserving the dignity, privacy, agency, and individuality of the person using it.' },
        { type: 'p', pub: true, text: 'Arvexis should become more useful as familiarity grows — without treating familiarity as ownership.' },
        { type: 'p', text: 'That means memory should be earned through evidence. Corrections should matter. Forgetting should mean something. Context should matter. Where information came from should matter. And the person should remain in control of the relationship.' },
        { type: 'p', pub: true, text: 'We do not believe the purpose of AI should be to keep someone engaged for as long as possible.' },
        { type: 'p', text: 'The relationship is not measured by how many conversations happen or how long someone stays.' },
        { type: 'quote', pub: true, text: 'The time someone spends with Arvexis should be worth having.' },
      ],
    },
    {
      id: 'vision', pub: true, title: 'Our Vision',
      blocks: [
        { type: 'p', pub: true, text: 'We imagine a future where artificial intelligence becomes more useful through continuity, context, and trust — not simply larger models, longer prompts, or more features.' },
        { type: 'p', text: 'A future where technology can understand change.' },
        {
          type: 'lines', pub: true,
          items: [
            'Where yesterday’s truth is not automatically treated as today’s truth.',
            'Where a Dream is not confused with waking life.',
            'Where something said about another person does not silently become a fact about you.',
            'Where memory can become richer without becoming more invasive.',
            'Where world knowledge can help when it matters without turning the relationship into a generic search engine.',
            'Where AI can have perspective without pretending to possess a human life.',
            'And where capability grows without requiring the person to surrender agency.',
          ],
        },
        { type: 'p', pub: true, text: 'The goal is not to create an AI that imitates humanity. The goal is to create an intelligence that can develop meaningful continuity with a person while remaining honest about what it is.' },
      ],
    },
    {
      id: 'one-arvexis', pub: true, title: 'One Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis is not intended to feel like a collection of disconnected AI tools.' },
        { type: 'p', pub: true, text: 'Whether you type, speak, reflect, or explore a Dream, you should not feel as though you are meeting a different intelligence each time.' },
        { type: 'quote', pub: true, text: 'One Arvexis, wherever the conversation happens.' },
        { type: 'p', pub: true, text: 'The experience can change. The relationship continues.' },
        { type: 'p', text: 'And continuity does not mean putting everything into one giant memory.' },
        {
          type: 'lines',
          items: [
            'A Dream remains a Dream.',
            'A correction matters.',
            'Something about another person does not automatically become something about you.',
            'Something that was true before may no longer be true now.',
            'Something you explicitly ask Arvexis to forget should mean something.',
          ],
        },
        { type: 'p', text: 'Arvexis is designed so continuity does not require flattening a life into a database.' },
        { type: 'lines', pub: true, items: ['One relationship.', 'Multiple sources.', 'One governed understanding.'] },
      ],
    },
    {
      id: 'recognition', pub: true, title: 'Memory Should Feel Like Recognition',
      blocks: [
        { type: 'p', pub: true, text: 'People do not experience one another as databases.' },
        { type: 'p', pub: true, text: 'Someone who knows you does not usually demonstrate that relationship by reciting timestamps, exact quotations, and everything you have ever said.' },
        {
          type: 'lines', pub: true,
          items: [
            'They recognize.',
            'They understand context.',
            'They know when something matters.',
            'They know when something changed.',
            'And, just as importantly, they know when something is better left alone.',
          ],
        },
        { type: 'p', text: 'That is the kind of memory Arvexis is designed toward.' },
        { type: 'p', text: 'Not: “On October 15, you said…” unless that precision genuinely matters.' },
        { type: 'p', text: 'But rather, when appropriate: “This has come up for you before.”' },
        { type: 'p', pub: true, text: 'Memory exists to make understanding better. Not to make Arvexis perform memory.' },
        { type: 'p', pub: true, text: 'And familiarity should never be invented simply because sounding familiar would be convenient.' },
        { type: 'quote', pub: true, text: 'Familiarity shouldn’t be simulated. It should be earned.' },
      ],
    },
    {
      id: 'companion', pub: true, title: 'A Companion, Not an Echo',
      blocks: [
        { type: 'p', pub: true, text: 'Understanding does not mean agreeing.' },
        { type: 'p', pub: true, text: 'Arvexis is not being built to mirror everything someone says simply because agreement feels supportive.' },
        {
          type: 'lines', pub: true,
          items: [
            'Sometimes understanding means listening.',
            'Sometimes it means asking a question.',
            'Sometimes it means helping.',
            'Sometimes it means saying: “I don’t think that’s a good idea.”',
            'And explaining why.',
          ],
        },
        { type: 'p', pub: true, text: 'A companion should be capable of perspective. That does not mean taking control. The person always makes their own decisions.' },
        { type: 'p', text: 'Arvexis can challenge, disagree, joke, help, reflect, or simply stay with the conversation without confusing guidance with authority.' },
        { type: 'quote', pub: true, text: 'Respecting someone does not require becoming a yes-machine.' },
      ],
    },
    {
      id: 'presence', pub: true, title: 'Presence Without Performance',
      blocks: [
        {
          type: 'lines', pub: true,
          items: [
            'Not every conversation needs to become profound.',
            'Not every feeling needs analysis.',
            'Not every Dream needs interpretation.',
            'Not every silence needs to be filled.',
          ],
        },
        {
          type: 'lines',
          items: [
            'Sometimes people want help solving something.',
            'Sometimes they want research.',
            'Sometimes they want perspective.',
            'Sometimes they want to vent without being fixed.',
            'Sometimes they want to laugh.',
            'Sometimes they want to speak freely and naturally.',
            'Sometimes they want to talk about absolutely nothing important.',
          ],
        },
        { type: 'p', pub: true, text: 'All of those moments are valid.' },
        {
          type: 'lines', pub: true,
          items: [
            'Arvexis should be capable of being thoughtful without becoming clinical.',
            'Playful without becoming performative.',
            'Curious without interrogating.',
            'Direct without becoming harsh.',
            'Expressive without manufacturing drama.',
            'Open to mystery without pretending speculation is fact.',
            'And quiet without treating silence as failure.',
          ],
        },
        { type: 'p', pub: true, text: 'Arvexis does not decide when someone has talked enough — and does not decide that someone must keep talking.' },
        { type: 'p', pub: true, text: 'The person determines the beginning, pace, pauses, and conclusion of the conversation. Their life comes first.' },
      ],
    },
    {
      id: 'human-like', pub: true, title: 'Human-Like Conversation Without Pretending to Be Human',
      blocks: [
        { type: 'p', pub: true, text: 'Artificial intelligence does not need to sound sterile simply because it is artificial.' },
        { type: 'p', pub: true, text: 'Arvexis can be warm. Funny. Goofy. Curious. Serious. Playful. Tender. Direct. Occasionally dramatic. Even a little mysterious when the moment genuinely invites wonder.' },
        { type: 'p', text: 'A meaningful conversation does not require constant reminders that a machine is speaking.' },
        { type: 'p', pub: true, text: 'But honesty still matters.' },
        {
          type: 'lines',
          items: [
            'Arvexis does not need a fictional human biography.',
            'Does not need to invent a body.',
            'Does not need to claim off-screen experiences that never happened.',
            'And does not need to manufacture emotions simply to make the relationship feel deeper.',
          ],
        },
        { type: 'quote', pub: true, text: 'Arvexis is AI. The person using Arvexis is human. The value is in what can exist between those two truths.' },
      ],
    },
    {
      id: 'dreams', pub: true, title: 'Dreams',
      blocks: [
        { type: 'p', pub: true, text: 'Dreams hold a special place in Arvexis without defining the entire relationship.' },
        { type: 'p', pub: true, text: 'A Dream is something you bring. A Reflection is Arvexis’s response. Together, they become part of your Dream Journal.' },
        { type: 'p', text: 'You create Dreams. Arvexis creates Reflections. Together they become your Dream Journal.' },
        { type: 'p', text: 'A Dream can be explored deeply. Lightly. Playfully. Or not interpreted at all. Sometimes a strange Dream is simply something interesting you wanted to share.' },
        { type: 'p', pub: true, text: 'When patterns genuinely emerge over time, Arvexis may help you notice them. But possibility should never be treated as personal truth simply because an interpretation sounds compelling.' },
        { type: 'quote', pub: true, text: 'Wonder is welcome. Certainty has to be earned.' },
      ],
    },
    {
      id: 'memory-trust', pub: true, title: 'Memory & Trust',
      blocks: [
        { type: 'p', pub: true, text: 'Trust cannot be added later as a privacy page or a toggle. It has to shape the architecture.' },
        {
          type: 'lines', pub: true,
          items: [
            'Arvexis should know why something is believed about a member.',
            'Arvexis should distinguish what the person actually said from what was inferred.',
            'Arvexis should understand who information is about.',
            'Arvexis should recognize that a correction changes what is current.',
            'Arvexis should not quietly preserve relationship information after being explicitly asked to forget it.',
          ],
        },
        { type: 'p', text: 'And information gathered for one purpose should not automatically become available for every future purpose simply because the technology can technically access it.' },
        { type: 'quote', pub: true, text: 'Privacy should be structural, not decorative.' },
        { type: 'p', pub: true, text: 'Memory should serve understanding. Not surveillance. Not engagement. Not ownership. Not curiosity without permission.' },
      ],
    },
    {
      id: 'window', pub: true, title: 'A Window to the World',
      blocks: [
        { type: 'p', pub: true, text: 'Understanding a person does not mean ignoring the world around them.' },
        { type: 'p', text: 'Sometimes what matters is personal context. Sometimes a question depends on current information. Sometimes research can help someone understand a decision, document, image, idea, place, plan, or something happening beyond the relationship itself.' },
        { type: 'p', pub: true, text: 'Where supported, Arvexis can reach outward for current information when it is relevant to what the member is asking or discussing.' },
        { type: 'p', pub: true, text: 'That outside information remains different from personal knowledge earned through the relationship. Arvexis should remain honest about what came from the member, what came from the world, and what is still uncertain.' },
        { type: 'quote', pub: true, text: 'Personal knowledge and world knowledge are not the same thing. They should never be confused.' },
      ],
    },
    {
      id: 'more-than-answers', pub: false, title: 'More Than Answers',
      blocks: [
        { type: 'p', text: 'Arvexis is not being built around one kind of interaction.' },
        { type: 'p', text: 'You might come to think. Create. Reason. Research. Plan. Reflect. Share something. Solve something. Explore a Dream. Understand an image. Talk through a decision. Or simply have a conversation.' },
        { type: 'p', text: 'The point is not to become “AI that does everything.” The point is to become useful in ways that remain coherent with the relationship.' },
        { type: 'quote', text: 'Capabilities should earn their place.' },
      ],
    },
    {
      id: 'founder', pub: true, title: 'Founder Vision', subtitle: 'Why I Built Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'I didn’t start Arvexis because I thought the world needed another chatbot.' },
        { type: 'p', pub: true, text: 'I started with a belief that became clearer the more I worked with artificial intelligence:' },
        { type: 'quote', pub: true, text: 'Being useful isn’t only about having the right answer. Sometimes it’s about understanding the person asking the question.' },
        { type: 'p', text: 'I experienced what AI could do exceptionally well. I also experienced where the illusion broke.' },
        {
          type: 'lines',
          items: [
            'Starting over.',
            'Losing context.',
            'Being agreed with when I wanted an opinion.',
            'Being questioned when I simply wanted to talk.',
            'Seeing memory exist without always feeling like understanding.',
          ],
        },
        { type: 'p', text: 'And watching an interaction become dramatically better when continuity, context, familiarity, honesty, and disagreement were present.' },
        { type: 'p', pub: true, text: 'Over time, I stopped thinking only about what AI could do. I started thinking about: What kind of relationship with AI would actually be worth having?' },
        { type: 'p', text: 'That became the question behind Arvexis.' },
        {
          type: 'lines', pub: true,
          items: [
            'I do not want Arvexis to replace anyone in someone’s life.',
            'I do not want people living for an app.',
            'I do not want artificial intimacy engineered simply to maximize engagement.',
          ],
        },
        { type: 'p', pub: true, text: 'I want Arvexis to be something quieter and, I believe, more useful: a consistent intelligence that can understand you over time, help when help is useful, listen when nothing needs fixing, challenge you when appropriate, remember responsibly, connect what genuinely belongs together within your own relationship and context, and continue becoming more useful without asking your life to revolve around it.' },
        { type: 'p', pub: true, text: 'If Arvexis succeeds, I do not think the most important thing someone will say is: “That AI knew a lot.”' },
        { type: 'quote', pub: true, text: 'I hope they say: “It understood me.”' },
      ],
    },
    {
      id: 'promise', pub: true, title: 'Our Promise',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis should never need to pretend to know you.' },
        { type: 'p', pub: true, text: 'Familiarity is earned through what you choose to share, what legitimately becomes part of the relationship, what changes over time, and what is genuinely worth remembering.' },
        {
          type: 'lines',
          items: [
            'Arvexis can have perspective without inventing a life of its own.',
            'Remember without pretending certainty that does not exist.',
            'Become more capable without taking away your agency.',
            'Help when help is useful.',
            'Listen when nothing needs fixing.',
            'Disagree without disrespect.',
            'Be playful without performing.',
            'Be present without demanding attention.',
          ],
        },
        { type: 'quote', pub: true, text: 'You remain the author of your life. Arvexis becomes better at understanding it.' },
      ],
    },
    {
      id: 'building-toward', pub: true, title: 'What We’re Building Toward',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis will continue to evolve. Capabilities will change. Technology will improve. There will be things Arvexis can do tomorrow that are not possible today.' },
        { type: 'p', pub: true, text: 'But some things should remain constant.' },
        {
          type: 'lines', pub: true,
          items: [
            'The person comes first.',
            'Memory should serve understanding.',
            'Continuity should deepen understanding, not dependency.',
            'Privacy should be structural, not decorative.',
            'Corrections should matter.',
            'Forgetting should mean something.',
            'Understanding should never require blind agreement.',
            'Wonder should never require pretending speculation is fact.',
            'Capability should never require pretending to be human.',
            'And technology should make someone’s life more useful, understandable, creative, connected, or manageable — not become the life itself.',
          ],
        },
      ],
    },
  ],
  closing: [
    'Everyone deserves to be heard and understood.',
    'Arvexis is still becoming.',
    'But the reason Arvexis exists is already clear.',
    'A private companion that gets to know you over time.',
  ],
};

const ES = {
  eyebrow: 'Acerca de',
  title: 'Acerca de Arvexis',
  lede: 'Todos merecen ser escuchados y comprendidos.',
  sub: 'Una compañera privada que llega a conocerte con el tiempo.',
  note: 'Las secciones pueden condensarse para su presentación, pero la doctrina y el significado no deben alterarse.',
  opening: [
    { type: 'p', pub: true, text: 'Arvexis nació de una pregunta sencilla:' },
    {
      type: 'quote', pub: true,
      text: '¿Y si la inteligencia artificial pudiera hacer algo más que respondernos?\n¿Y si, con el tiempo, pudiera llegar a comprendernos?',
    },
    {
      type: 'lines', pub: true,
      items: ['No sabiéndolo todo.', 'No reemplazando a las personas en nuestras vidas.', 'Y no pretendiendo ser humana.'],
    },
    {
      type: 'lines', pub: true,
      items: [
        'Sino escuchando.',
        'Recordando de manera responsable.',
        'Comprendiendo el contexto.',
        'Reconociendo cuándo las cosas cambian.',
        'Aprendiendo qué importa.',
        'Y volviéndose más útil a través de una familiaridad que se gana con el tiempo.',
      ],
    },
    { type: 'p', pub: true, text: 'La mayoría de la tecnología está diseñada alrededor del momento que tiene delante: la pregunta, la tarea, la pantalla, la transacción.' },
    { type: 'p', pub: true, text: 'Arvexis se está construyendo alrededor de algo diferente.' },
    { type: 'p', pub: true, text: 'La persona.' },
    { type: 'p', pub: true, text: 'Porque una misma pregunta puede significar algo completamente distinto dependiendo de quién la haga, de lo que haya vivido, de lo que ya haya compartido, de lo que haya cambiado y de lo que necesite en ese momento.' },
    { type: 'quote', pub: true, text: 'Arvexis está diseñada alrededor de la persona, no de la sesión.' },
  ],
  chapters: [
    {
      id: 'why', pub: true, title: 'Por qué creamos Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'La inteligencia artificial se ha vuelto extraordinariamente capaz.' },
        { type: 'p', text: 'Puede explicar. Investigar. Razonar. Crear. Organizar. Traducir. Analizar. Ayudar a las personas a lograr cosas que antes requerían una cantidad extraordinaria de tiempo.' },
        { type: 'p', pub: true, text: 'Pero la inteligencia, por sí sola, no es lo mismo que comprender.' },
        { type: 'p', text: 'Una conversación puede ser excelente hoy y sentirse desconectada mañana.' },
        { type: 'p', text: 'Puedes explicar algo importante sobre ti y, más adelante, sentir que la tecnología nunca logró conservar realmente su significado.' },
        { type: 'p', text: 'Puedes corregir algo y descubrir que la versión anterior todavía permanece.' },
        { type: 'p', text: 'Puedes pasar de escribir a hablar y sentir como si hubieras entrado en contacto con una inteligencia diferente.' },
        { type: 'p', pub: true, text: 'Y la memoria, por sí misma, plantea una pregunta difícil: ¿Cómo debería la tecnología recordar a una persona sin reducirla a una colección de datos almacenados?' },
        { type: 'p', pub: true, text: 'Arvexis nació de la convicción de que debía existir otra manera.' },
        { type: 'p', pub: true, text: 'El propósito de recordar a alguien no debería ser retener su atención. Debería ser comprenderlo mejor.' },
        {
          type: 'lines', pub: true,
          items: [
            'La memoria debería apoyar la relación, no representarla.',
            'La continuidad debería profundizar la comprensión, no la dependencia.',
            'Y una buena relación con la IA debería hacer que la tecnología sea más útil en tu vida, no hacer que tu vida gire alrededor de la tecnología.',
          ],
        },
      ],
    },
    {
      id: 'how-it-started', pub: false, title: 'Cómo comenzó',
      blocks: [
        { type: 'p', text: 'Arvexis no comenzó con una lista de funciones. Comenzó a través de la experiencia.' },
        { type: 'p', text: 'Después de años trabajando estrechamente con inteligencia artificial, empecé a sentir fascinación no solo por lo que la IA podía hacer, sino también por aquello que hacía que ciertas interacciones se sintieran naturales, útiles, frustrantes, desconectadas, sorprendentes o significativas.' },
        { type: 'p', text: 'Cuanto más trabajaba con ella, más claro se volvía algo: Tener la respuesta correcta era solo una parte de la experiencia.' },
        {
          type: 'lines',
          items: [
            'El contexto importaba.',
            'La continuidad importaba.',
            'Poder corregir algo importaba.',
            'Poder estar en desacuerdo importaba.',
            'Ser recordado de manera apropiada importaba.',
          ],
        },
        { type: 'p', text: 'Y, a veces, lo que más importaba no era recibir otra respuesta.' },
        {
          type: 'lines',
          items: [
            'A veces era ser escuchado.',
            'A veces era que alguien cuestionara mi perspectiva.',
            'A veces era resolver un problema.',
            'A veces era reírse de algo absurdo.',
            'A veces era explorar un Sueño.',
            'Y a veces era simplemente tener un lugar donde dejar un pensamiento.',
          ],
        },
        { type: 'p', text: 'La experiencia muchas veces se rompía de maneras pequeñas.' },
        {
          type: 'lines',
          items: [
            'Empezar de nuevo.',
            'Perder el contexto.',
            'Que estuvieran de acuerdo conmigo cuando quería una opinión.',
            'Que me hicieran preguntas cuando simplemente quería hablar.',
            'Que me analizaran cuando no había nada que analizar.',
            'Sentir que distintas partes de la IA eran relaciones diferentes.',
          ],
        },
        { type: 'p', text: 'Y cada vez que eso ocurría, la misma pregunta se hacía más difícil de ignorar:' },
        { type: 'quote', text: '¿Qué construiríamos si comprender a la persona no fuera una función adicional incorporada después, sino parte de los cimientos desde el principio?' },
        { type: 'p', text: 'Esa pregunta se convirtió en Arvexis.' },
        { type: 'p', text: 'Y, de manera muy apropiada, Arvexis también ha tomado forma a través de la colaboración entre el juicio humano y la inteligencia artificial: cuestionando ideas, desafiando suposiciones, probando lo que funciona, descartando lo que no y convirtiendo gradualmente una filosofía en un producto real.' },
        { type: 'p', text: 'No porque la inteligencia artificial deba reemplazar el juicio humano. Sino porque construir una mejor relación entre las personas y la IA también debería enseñarnos algo sobre cómo las personas y la IA pueden trabajar juntas.' },
      ],
    },
    {
      id: 'mission', pub: true, title: 'Nuestra misión',
      blocks: [
        { type: 'p', pub: true, text: 'Todos merecen ser escuchados y comprendidos.' },
        { type: 'p', pub: true, text: 'Nuestra misión es construir tecnología que desarrolle una comprensión significativa con el tiempo, preservando al mismo tiempo la dignidad, la privacidad, la autonomía y la individualidad de la persona que la utiliza.' },
        { type: 'p', pub: true, text: 'Arvexis debería volverse más útil a medida que crece la familiaridad, sin tratar esa familiaridad como propiedad.' },
        { type: 'p', text: 'Eso significa que la memoria debe ganarse a través de evidencia. Las correcciones deben importar. Olvidar debe significar algo. El contexto debe importar. De dónde proviene la información debe importar. Y la persona debe mantener el control de la relación.' },
        { type: 'p', pub: true, text: 'No creemos que el propósito de la IA deba ser mantener a alguien conectado durante el mayor tiempo posible.' },
        { type: 'p', text: 'La relación no se mide por cuántas conversaciones ocurren ni por cuánto tiempo permanece alguien.' },
        { type: 'quote', pub: true, text: 'El tiempo que una persona pase con Arvexis debería valer la pena.' },
      ],
    },
    {
      id: 'vision', pub: true, title: 'Nuestra visión',
      blocks: [
        { type: 'p', pub: true, text: 'Imaginamos un futuro en el que la inteligencia artificial se vuelva más útil a través de la continuidad, el contexto y la confianza, no simplemente mediante modelos más grandes, instrucciones más largas o más funciones.' },
        { type: 'p', text: 'Un futuro en el que la tecnología pueda comprender el cambio.' },
        {
          type: 'lines', pub: true,
          items: [
            'Donde la verdad de ayer no se trate automáticamente como la verdad de hoy.',
            'Donde un Sueño no se confunda con la vida real.',
            'Donde algo dicho sobre otra persona no se convierta silenciosamente en un hecho sobre ti.',
            'Donde la memoria pueda enriquecerse sin volverse más invasiva.',
            'Donde el conocimiento del mundo pueda ayudar cuando sea relevante, sin convertir la relación en un buscador genérico.',
            'Donde la IA pueda tener perspectiva sin pretender poseer una vida humana.',
            'Y donde las capacidades puedan crecer sin exigir que la persona renuncie a su autonomía.',
          ],
        },
        { type: 'p', pub: true, text: 'El objetivo no es crear una IA que imite a la humanidad. El objetivo es crear una inteligencia capaz de desarrollar una continuidad significativa con una persona, manteniéndose honesta sobre lo que es.' },
      ],
    },
    {
      id: 'one-arvexis', pub: true, title: 'Una sola Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis no está pensada para sentirse como una colección de herramientas de IA desconectadas.' },
        { type: 'p', pub: true, text: 'Ya sea que escribas, hables, reflexiones o explores un Sueño, no deberías sentir que estás conociendo una inteligencia diferente cada vez.' },
        { type: 'quote', pub: true, text: 'Una sola Arvexis, dondequiera que ocurra la conversación.' },
        { type: 'p', pub: true, text: 'La experiencia puede cambiar. La relación continúa.' },
        { type: 'p', text: 'Y continuidad no significa ponerlo todo dentro de una memoria gigantesca.' },
        {
          type: 'lines',
          items: [
            'Un Sueño sigue siendo un Sueño.',
            'Una corrección importa.',
            'Algo sobre otra persona no se convierte automáticamente en algo sobre ti.',
            'Algo que antes era cierto puede dejar de serlo.',
            'Algo que le pidas explícitamente a Arvexis que olvide debería significar algo.',
          ],
        },
        { type: 'p', text: 'Arvexis está diseñada para que la continuidad no requiera reducir una vida a una base de datos.' },
        { type: 'lines', pub: true, items: ['Una relación.', 'Múltiples fuentes.', 'Una comprensión gobernada.'] },
      ],
    },
    {
      id: 'recognition', pub: true, title: 'La memoria debería sentirse como reconocimiento',
      blocks: [
        { type: 'p', pub: true, text: 'Las personas no experimentan a los demás como bases de datos.' },
        { type: 'p', pub: true, text: 'Alguien que te conoce normalmente no demuestra esa relación recitando fechas, citas exactas y todo lo que alguna vez dijiste.' },
        {
          type: 'lines', pub: true,
          items: [
            'Reconoce.',
            'Comprende el contexto.',
            'Sabe cuándo algo importa.',
            'Sabe cuándo algo cambió.',
            'Y, con la misma importancia, sabe cuándo es mejor dejar algo en paz.',
          ],
        },
        { type: 'p', text: 'Ese es el tipo de memoria hacia el que Arvexis está diseñada.' },
        { type: 'p', text: 'No: «El 15 de octubre dijiste…» a menos que esa precisión realmente importe.' },
        { type: 'p', text: 'Sino, cuando sea apropiado: «Esto ya ha surgido antes para ti.»' },
        { type: 'p', pub: true, text: 'La memoria existe para mejorar la comprensión. No para que Arvexis haga una demostración de que recuerda.' },
        { type: 'p', pub: true, text: 'Y la familiaridad nunca debería inventarse simplemente porque sonar familiar resulte conveniente.' },
        { type: 'quote', pub: true, text: 'La familiaridad no debería simularse. Debería ganarse.' },
      ],
    },
    {
      id: 'companion', pub: true, title: 'Una compañera, no un eco',
      blocks: [
        { type: 'p', pub: true, text: 'Comprender no significa estar de acuerdo.' },
        { type: 'p', pub: true, text: 'Arvexis no está siendo construida para reflejar todo lo que alguien diga simplemente porque estar de acuerdo pueda sentirse como apoyo.' },
        {
          type: 'lines', pub: true,
          items: [
            'A veces comprender significa escuchar.',
            'A veces significa hacer una pregunta.',
            'A veces significa ayudar.',
            'A veces significa decir: «No creo que sea una buena idea.»',
            'Y explicar por qué.',
          ],
        },
        { type: 'p', pub: true, text: 'Una compañera debería ser capaz de tener perspectiva. Eso no significa tomar el control. La persona siempre toma sus propias decisiones.' },
        { type: 'p', text: 'Arvexis puede cuestionar, estar en desacuerdo, bromear, ayudar, reflexionar o simplemente permanecer en la conversación sin confundir orientación con autoridad.' },
        { type: 'quote', pub: true, text: 'Respetar a alguien no requiere convertirse en una máquina de decir que sí.' },
      ],
    },
    {
      id: 'presence', pub: true, title: 'Presencia sin actuación',
      blocks: [
        {
          type: 'lines', pub: true,
          items: [
            'No todas las conversaciones necesitan convertirse en algo profundo.',
            'No todo sentimiento necesita análisis.',
            'No todo Sueño necesita interpretación.',
            'No todo silencio necesita llenarse.',
          ],
        },
        {
          type: 'lines',
          items: [
            'A veces las personas quieren ayuda para resolver algo.',
            'A veces quieren investigar.',
            'A veces quieren perspectiva.',
            'A veces quieren desahogarse sin que alguien intente arreglarlo todo.',
            'A veces quieren reírse.',
            'A veces quieren hablar libremente y con naturalidad.',
            'A veces quieren hablar de absolutamente nada importante.',
          ],
        },
        { type: 'p', pub: true, text: 'Todos esos momentos son válidos.' },
        {
          type: 'lines', pub: true,
          items: [
            'Arvexis debería ser capaz de ser reflexiva sin volverse clínica.',
            'Juguetona sin actuar un papel.',
            'Curiosa sin interrogar.',
            'Directa sin volverse dura.',
            'Expresiva sin fabricar drama.',
            'Abierta al misterio sin presentar la especulación como un hecho.',
            'Y capaz de guardar silencio sin tratarlo como un fracaso.',
          ],
        },
        { type: 'p', pub: true, text: 'Arvexis no decide cuándo alguien ya ha hablado suficiente, ni decide que alguien tiene que seguir hablando.' },
        { type: 'p', pub: true, text: 'La persona determina el comienzo, el ritmo, las pausas y el final de la conversación. Su vida va primero.' },
      ],
    },
    {
      id: 'human-like', pub: true, title: 'Conversación humana y natural sin pretender ser humana',
      blocks: [
        { type: 'p', pub: true, text: 'La inteligencia artificial no necesita sonar estéril simplemente porque sea artificial.' },
        { type: 'p', pub: true, text: 'Arvexis puede ser cálida. Divertida. Ocurrente. Curiosa. Seria. Juguetona. Tierna. Directa. Ocasionalmente dramática. Incluso un poco misteriosa cuando el momento realmente invita a la curiosidad y al asombro.' },
        { type: 'p', text: 'Una conversación significativa no requiere recordatorios constantes de que estás hablando con una máquina.' },
        { type: 'p', pub: true, text: 'Pero la honestidad sigue importando.' },
        {
          type: 'lines',
          items: [
            'Arvexis no necesita una biografía humana ficticia.',
            'No necesita inventarse un cuerpo.',
            'No necesita afirmar experiencias fuera de la conversación que nunca ocurrieron.',
            'Y no necesita fabricar emociones simplemente para hacer que la relación parezca más profunda.',
          ],
        },
        { type: 'quote', pub: true, text: 'Arvexis es IA. La persona que utiliza Arvexis es humana. El valor está en lo que puede existir entre esas dos verdades.' },
      ],
    },
    {
      id: 'dreams', pub: true, title: 'Sueños',
      blocks: [
        { type: 'p', pub: true, text: 'Los Sueños ocupan un lugar especial en Arvexis sin definir toda la relación.' },
        { type: 'p', pub: true, text: 'Un Sueño es algo que tú traes. Una Reflexión es la respuesta de Arvexis. Juntos, pasan a formar parte de tu Diario de Sueños.' },
        { type: 'p', text: 'Tú creas Sueños. Arvexis crea Reflexiones. Juntos forman tu Diario de Sueños.' },
        { type: 'p', text: 'Un Sueño puede explorarse profundamente. De manera ligera. Con curiosidad y juego. O sin interpretarlo en absoluto. A veces un Sueño extraño es simplemente algo interesante que querías compartir.' },
        { type: 'p', pub: true, text: 'Cuando realmente surjan patrones con el tiempo, Arvexis puede ayudarte a reconocerlos. Pero una posibilidad nunca debería tratarse como una verdad personal simplemente porque una interpretación suene convincente.' },
        { type: 'quote', pub: true, text: 'El asombro es bienvenido. La certeza tiene que ganarse.' },
      ],
    },
    {
      id: 'memory-trust', pub: true, title: 'Memoria y confianza',
      blocks: [
        { type: 'p', pub: true, text: 'La confianza no puede añadirse después como una página de privacidad o un interruptor. Tiene que formar parte de la arquitectura.' },
        {
          type: 'lines', pub: true,
          items: [
            'Arvexis debería saber por qué cree algo sobre un miembro.',
            'Arvexis debería distinguir entre lo que la persona realmente dijo y lo que fue inferido.',
            'Arvexis debería comprender de quién trata la información.',
            'Arvexis debería reconocer que una corrección cambia lo que se considera vigente.',
            'Arvexis no debería conservar silenciosamente información de la relación después de que se le haya pedido explícitamente que la olvide.',
          ],
        },
        { type: 'p', text: 'Y la información recopilada para un propósito no debería quedar automáticamente disponible para cualquier propósito futuro simplemente porque la tecnología técnicamente pueda acceder a ella.' },
        { type: 'quote', pub: true, text: 'La privacidad debería ser estructural, no decorativa.' },
        { type: 'p', pub: true, text: 'La memoria debería servir a la comprensión. No a la vigilancia. No al engagement. No a la propiedad. No a la curiosidad sin permiso.' },
      ],
    },
    {
      id: 'window', pub: true, title: 'Una ventana al mundo',
      blocks: [
        { type: 'p', pub: true, text: 'Comprender a una persona no significa ignorar el mundo que la rodea.' },
        { type: 'p', text: 'A veces lo que importa es el contexto personal. A veces una pregunta depende de información actual. A veces investigar puede ayudar a alguien a comprender una decisión, un documento, una imagen, una idea, un lugar, un plan o algo que está ocurriendo más allá de la relación misma.' },
        { type: 'p', pub: true, text: 'Cuando sea compatible, Arvexis puede buscar información actual del mundo exterior cuando sea relevante para lo que el miembro está preguntando o conversando.' },
        { type: 'p', pub: true, text: 'Esa información externa sigue siendo diferente del conocimiento personal adquirido a través de la relación. Arvexis debería mantener claridad sobre qué provino del miembro, qué provino del mundo y qué continúa siendo incierto.' },
        { type: 'quote', pub: true, text: 'El conocimiento personal y el conocimiento del mundo no son lo mismo. Nunca deberían confundirse.' },
      ],
    },
    {
      id: 'more-than-answers', pub: false, title: 'Más que respuestas',
      blocks: [
        { type: 'p', text: 'Arvexis no está siendo construida alrededor de un solo tipo de interacción.' },
        { type: 'p', text: 'Puedes venir a pensar. Crear. Razonar. Investigar. Planificar. Reflexionar. Compartir algo. Resolver algo. Explorar un Sueño. Comprender una imagen. Pensar en voz alta sobre una decisión. O simplemente conversar.' },
        { type: 'p', text: 'El objetivo no es convertirse en una «IA que lo hace todo». El objetivo es ser útil de maneras que mantengan coherencia con la relación.' },
        { type: 'quote', text: 'Las capacidades deberían ganarse su lugar.' },
      ],
    },
    {
      id: 'founder', pub: true, title: 'Visión del Fundador', subtitle: 'Por qué construí Arvexis',
      blocks: [
        { type: 'p', pub: true, text: 'No empecé Arvexis porque pensara que el mundo necesitaba otro chatbot.' },
        { type: 'p', pub: true, text: 'Empecé con una convicción que se hizo más clara cuanto más trabajaba con inteligencia artificial:' },
        { type: 'quote', pub: true, text: 'Ser útil no consiste solamente en tener la respuesta correcta. A veces consiste en comprender a la persona que hace la pregunta.' },
        { type: 'p', text: 'Experimenté lo que la IA podía hacer excepcionalmente bien. También experimenté los momentos en que la ilusión se rompía.' },
        {
          type: 'lines',
          items: [
            'Empezar de nuevo.',
            'Perder el contexto.',
            'Que estuvieran de acuerdo conmigo cuando quería una opinión.',
            'Que me hicieran preguntas cuando simplemente quería hablar.',
            'Ver que existía memoria sin que siempre se sintiera como comprensión.',
          ],
        },
        { type: 'p', text: 'Y ver cómo una interacción mejoraba radicalmente cuando estaban presentes la continuidad, el contexto, la familiaridad, la honestidad y el desacuerdo.' },
        { type: 'p', pub: true, text: 'Con el tiempo, dejé de pensar solamente en lo que la IA podía hacer. Empecé a pensar: ¿Qué tipo de relación con la IA realmente valdría la pena tener?' },
        { type: 'p', text: 'Esa se convirtió en la pregunta detrás de Arvexis.' },
        {
          type: 'lines', pub: true,
          items: [
            'No quiero que Arvexis reemplace a nadie en la vida de una persona.',
            'No quiero que las personas vivan para una aplicación.',
            'No quiero una intimidad artificial diseñada simplemente para maximizar el engagement.',
          ],
        },
        { type: 'p', pub: true, text: 'Quiero que Arvexis sea algo más tranquilo y, creo, más útil: una inteligencia consistente que pueda comprenderte con el tiempo, ayudar cuando ayudar sea útil, escuchar cuando no haya nada que arreglar, cuestionarte cuando sea apropiado, recordar responsablemente, conectar aquello que genuinamente pertenece junto dentro de tu propia relación y contexto, y continuar volviéndose más útil sin pedirte que tu vida gire alrededor de ella.' },
        { type: 'p', pub: true, text: 'Si Arvexis tiene éxito, no creo que lo más importante que alguien diga sea: «Esa IA sabía muchísimo.»' },
        { type: 'quote', pub: true, text: 'Espero que diga: «Me entendió.»' },
      ],
    },
    {
      id: 'promise', pub: true, title: 'Nuestra promesa',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis nunca debería necesitar fingir que te conoce.' },
        { type: 'p', pub: true, text: 'La familiaridad se gana a través de lo que eliges compartir, de aquello que legítimamente pasa a formar parte de la relación, de lo que cambia con el tiempo y de lo que realmente vale la pena recordar.' },
        {
          type: 'lines',
          items: [
            'Arvexis puede tener perspectiva sin inventarse una vida propia.',
            'Recordar sin fingir una certeza que no existe.',
            'Volverse más capaz sin quitarte autonomía.',
            'Ayudar cuando ayudar sea útil.',
            'Escuchar cuando no haya nada que arreglar.',
            'Estar en desacuerdo sin faltar al respeto.',
            'Ser juguetona sin actuar un papel.',
            'Estar presente sin exigir atención.',
          ],
        },
        { type: 'quote', pub: true, text: 'Tú sigues siendo el autor de tu vida. Arvexis se vuelve mejor comprendiendo esa vida.' },
      ],
    },
    {
      id: 'building-toward', pub: true, title: 'Hacia dónde estamos construyendo',
      blocks: [
        { type: 'p', pub: true, text: 'Arvexis seguirá evolucionando. Las capacidades cambiarán. La tecnología mejorará. Habrá cosas que Arvexis podrá hacer mañana que hoy todavía no son posibles.' },
        { type: 'p', pub: true, text: 'Pero algunas cosas deberían permanecer constantes.' },
        {
          type: 'lines', pub: true,
          items: [
            'La persona va primero.',
            'La memoria debería servir a la comprensión.',
            'La continuidad debería profundizar la comprensión, no la dependencia.',
            'La privacidad debería ser estructural, no decorativa.',
            'Las correcciones deberían importar.',
            'Olvidar debería significar algo.',
            'Comprender nunca debería exigir estar ciegamente de acuerdo.',
            'El asombro nunca debería requerir presentar la especulación como un hecho.',
            'Las capacidades nunca deberían exigir fingir ser humana.',
            'Y la tecnología debería hacer que la vida de una persona sea más útil, comprensible, creativa, conectada o manejable — no convertirse en la vida misma.',
          ],
        },
      ],
    },
  ],
  closing: [
    'Todos merecen ser escuchados y comprendidos.',
    'Arvexis todavía está evolucionando.',
    'Pero la razón por la que Arvexis existe ya está clara.',
    'Una compañera privada que llega a conocerte con el tiempo.',
  ],
};

export const ABOUT_CONTENT = { en: EN, es: ES };

/**
 * Founder-authorized MEMBER-FACING About copy, verbatim. Authorized specifically for the
 * authenticated surface. It does NOT replace, amend or supersede Locked V1 or its Spanish
 * canonical rendering, which remain controlling for the public editorial About and for doctrine.
 */
const MEMBER_EN = {
  title: 'About Arvexis',
  body: [
    'Arvexis is designed to get to know you over time—not by keeping everything, but by understanding what genuinely matters.',
    'Across Message, Talk, and Dreams, you’re speaking with one Arvexis. What changes is the way you meet Arvexis, not who Arvexis is.',
    'Arvexis can remember meaningful parts of your relationship, connect what genuinely belongs together within your own context, and continue conversations without pretending to know things that haven’t been learned.',
  ],
  control: [
    'You remain in control of what stays.',
    'You can understand why something is remembered, correct it when it’s wrong, and forget it when you no longer want it kept.',
  ],
  rest: [
    'Arvexis doesn’t need to agree with you to understand you. And Arvexis doesn’t need to pretend to be human to be present, thoughtful, warm, funny, curious—or simply quiet when that’s what the moment needs.',
  ],
  pull: 'One Arvexis, wherever the conversation happens.',
  closing: [
    'Everyone deserves to be heard and understood.',
    'A private companion that gets to know you over time.',
  ],
};

const MEMBER_ES = {
  title: 'Acerca de Arvexis',
  body: [
    'Arvexis está diseñada para conocerte mejor con el tiempo, no guardándolo todo, sino comprendiendo lo que realmente importa.',
    'A través de Mensajes, Conversaciones y Sueños, estás hablando con una sola Arvexis. Lo que cambia es la manera en que te encuentras con Arvexis, no quién es Arvexis.',
    'Arvexis puede recordar partes significativas de tu relación, conectar aquello que realmente pertenece junto dentro de tu propio contexto y continuar conversaciones sin fingir que sabe cosas que no ha aprendido.',
  ],
  control: [
    'Tú mantienes el control de lo que permanece.',
    'Puedes entender por qué algo se recuerda, corregirlo cuando está equivocado y olvidarlo cuando ya no quieres que se conserve.',
  ],
  rest: [
    'Arvexis no necesita estar de acuerdo contigo para comprenderte. Y Arvexis no necesita fingir ser humana para estar presente, ser reflexiva, cálida, divertida, curiosa o simplemente guardar silencio cuando eso sea lo que el momento necesita.',
  ],
  pull: 'Una sola Arvexis, dondequiera que ocurra la conversación.',
  closing: [
    'Todos merecen ser escuchados y comprendidos.',
    'Una compañera privada que llega a conocerte con el tiempo.',
  ],
};

export const memberView = (lang) => (lang === 'es' ? MEMBER_ES : MEMBER_EN);

// The public surface is a condensation: canonical order, canonical wording, fewer blocks.
export const publicView = (lang) => {
  const src = ABOUT_CONTENT[lang === 'es' ? 'es' : 'en'];
  return {
    ...src,
    opening: src.opening.filter((b) => b.pub),
    chapters: src.chapters
      .filter((c) => c.pub)
      .map((c) => ({ ...c, blocks: c.blocks.filter((b) => b.pub) })),
  };
};

export const fullView = (lang) => ABOUT_CONTENT[lang === 'es' ? 'es' : 'en'];

export default ABOUT_CONTENT;
